import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import {
  Radio, Users, MousePointerClick, MessageCircle, TrendingUp,
  Eye, Clock, ArrowUpRight, RefreshCw,
} from "lucide-react";

/**
 * Widget "Ao vivo no site" — mostra em tempo real quem está no site,
 * em qual página, cliques em WhatsApp com contexto, top páginas, origem
 * de tráfego. Puxado do backend próprio (beacon endpoint), não do GA4.
 *
 * Auto-refresh a cada 10s enquanto o admin tá com a aba aberta.
 */
const REFRESH_MS = 10000;

const humanTime = (secs) => {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  return `${h}h${m % 60}min`;
};

const REFERRER_META = {
  direto: { label: "Direto", color: "bg-slate-100 text-slate-700 border-slate-200" },
  google_organico: { label: "Google (orgânico)", color: "bg-blue-100 text-blue-800 border-blue-200" },
  google_ads: { label: "Google Ads", color: "bg-amber-100 text-amber-800 border-amber-200" },
  whatsapp: { label: "WhatsApp", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  instagram: { label: "Instagram", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  facebook: { label: "Facebook", color: "bg-sky-100 text-sky-800 border-sky-200" },
  tiktok: { label: "TikTok", color: "bg-rose-100 text-rose-800 border-rose-200" },
  bing: { label: "Bing", color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  outro: { label: "Outro", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const RefChip = ({ referrer }) => {
  const meta = REFERRER_META[referrer] || REFERRER_META.outro;
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest ${meta.color} border px-1.5 py-0.5 rounded-full font-semibold`}>
      {meta.label}
    </span>
  );
};

export const AnalyticsLivePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/analytics/live");
      setData(r.data);
      setLastRefresh(new Date());
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const kpis = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Ao vivo agora", value: data.live_count, icon: Radio, color: "text-red-600 bg-red-50 border-red-200", pulse: true },
      { label: "Visitantes 24h", value: data.unique_visitors_24h, icon: Users, color: "text-blue-700 bg-blue-50 border-blue-200" },
      { label: "Pageviews 24h", value: data.total_pageviews_24h, icon: Eye, color: "text-slate-700 bg-slate-50 border-slate-200" },
      { label: "Cliques WhatsApp 24h", value: data.wa_click_total_24h, icon: MessageCircle, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    ];
  }, [data]);

  if (loading && !data) {
    return <div className="p-10 text-slate-500 text-center" data-testid="analytics-live-loading">Carregando analytics em tempo real…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="analytics-live-panel">
      {/* Header + KPIs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#0B2B62] inline-flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
            </span>
            Ao vivo no site
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Dados coletados pelo backend RAF (independente do Google Analytics). Atualiza a cada 10s.
            {lastRefresh && <> · Última atualização {lastRefresh.toLocaleTimeString("pt-BR")}</>}
          </p>
        </div>
        <button onClick={load} data-testid="analytics-refresh"
          className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar agora
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className={`p-4 border rounded-xl ${k.color}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${k.pulse ? "animate-pulse" : ""}`} />
                <div className="text-[10px] uppercase tracking-widest font-semibold">{k.label}</div>
              </div>
              <div className="font-display font-bold text-3xl mt-1">{k.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sessões ao vivo */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-active-sessions">
          <header className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-600" />
              <h3 className="font-display font-bold text-[#0B2B62]">Sessões ativas ({data.live_count})</h3>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-slate-400">últimos 5 min</span>
          </header>
          {data.active_sessions.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              Nenhum visitante navegando neste momento.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {data.active_sessions.map((s) => (
                <li key={s.session_id} className="p-3 hover:bg-slate-50 text-sm" data-testid={`session-${s.session_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] bg-slate-900 text-white px-1.5 py-0.5 rounded">{s.session_id}</span>
                    <span className="font-mono text-xs text-[#0B2B62] font-semibold truncate">{s.path}</span>
                    <RefChip referrer={s.referrer} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {humanTime(s.seconds_on_site)} no site</span>
                    <span>{s.hits} evento{s.hits > 1 ? "s" : ""}</span>
                    <span>viu há {humanTime(s.seconds_since_last)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Páginas mais vistas agora */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-by-path-now">
          <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <h3 className="font-display font-bold text-[#0B2B62]">Páginas mais vistas agora</h3>
          </header>
          {data.by_path_now.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Sem visitantes ativos.</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {data.by_path_now.map((p) => (
                <li key={p.path} className="p-3 flex items-center justify-between hover:bg-slate-50">
                  <span className="font-mono text-xs text-[#0B2B62] truncate">{p.path}</span>
                  <span className="text-xs font-bold text-slate-700">{p.count} pessoa{p.count > 1 ? "s" : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* WhatsApp clicks */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-wa-clicks">
          <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            <h3 className="font-display font-bold text-[#0B2B62]">Cliques WhatsApp 24h ({data.wa_click_total_24h})</h3>
          </header>
          {data.whatsapp_clicks_24h.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              <MessageCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              Nenhum clique em botão WhatsApp nas últimas 24h.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {data.whatsapp_clicks_24h.map((w, i) => (
                <li key={i} className="p-3 hover:bg-slate-50 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">{w.cta}</span>
                    <span className="font-mono text-xs text-[#0B2B62] truncate">{w.path}</span>
                    <RefChip referrer={w.referrer} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {new Date(w.at).toLocaleString("pt-BR")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Origem de tráfego */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-referrers">
          <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-fuchsia-600" />
            <h3 className="font-display font-bold text-[#0B2B62]">Origem 24h</h3>
          </header>
          {data.referrers_24h.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">Sem dados nas últimas 24h.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.referrers_24h.map((r) => (
                <li key={r.referrer} className="p-3 flex items-center justify-between hover:bg-slate-50">
                  <RefChip referrer={r.referrer} />
                  <div className="text-xs text-slate-600">
                    <span className="font-bold text-slate-800">{r.unique_visitors}</span> visitantes ·{" "}
                    <span className="text-slate-500">{r.hits} hits</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Top paths 24h */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-top-paths">
        <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
          <MousePointerClick className="w-4 h-4 text-slate-600" />
          <h3 className="font-display font-bold text-[#0B2B62]">Páginas mais visitadas nas 24h</h3>
        </header>
        {data.top_paths_24h.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Sem pageviews nas últimas 24h ainda.</div>
        ) : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-2">Página</th>
                  <th className="px-4 py-2 text-right whitespace-nowrap">Visitantes</th>
                  <th className="px-4 py-2 text-right whitespace-nowrap">Pageviews</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.top_paths_24h.map((p) => (
                  <tr key={p.path} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-[#0B2B62] max-w-[520px] truncate" title={p.path}>{p.path}</td>
                    <td className="px-4 py-2 text-right font-bold text-slate-800 tabular-nums whitespace-nowrap">{p.unique_visitors}</td>
                    <td className="px-4 py-2 text-right text-slate-600 tabular-nums whitespace-nowrap">{p.pageviews}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AnalyticsLivePanel;
